
<template>
  <div class="container supplier-tabs-wrap">
    <div
      v-if="loading"
      class="core-loading-state"
      role="status"
    >
      Loading suppliers...
    </div>

    <div
      v-else-if="error"
      class="card-panel red lighten-4 red-text text-darken-4"
      role="alert"
    >
      {{ error }}

      <button
        type="button"
        class="btn-flat red-text text-darken-2"
        @click="loadSuppliers"
      >
        Try Again
      </button>
    </div>

    <div
      v-else-if="!suppliers.length"
      class="card-panel blue lighten-5 blue-text text-darken-4"
    >
      No suppliers are enabled for this venue.
      Contact Operations to request access.
    </div>

    <div
      v-else
      class="core-tabs supplier-tabs"
      role="tablist"
      aria-label="Select drinks supplier"
    >
      <button
        v-for="supplierName in suppliers"
        :key="supplierName"
        type="button"
        class="core-tab supplier-tab"
        :class="{
          active: modelValue === supplierName
        }"
        role="tab"
        :aria-selected="
          modelValue === supplierName
        "
        @click="selectSupplier(supplierName)"
      >
        {{ supplierName }}
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  onBeforeUnmount,
  ref,
  watch,
} from "vue";

import {
  fetchActiveSuppliers,
} from "@/scripts/sqlApiHelper/stockUnitsApi";

import {
  getVenueSupplierAccess,
  normaliseSupplierNames,
  supplierKey,
} from "@/scripts/sqlApiHelper/venueSuppliersApi";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },

  venueId: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "update:modelValue",
]);

const suppliers = ref([]);
const loading = ref(false);
const error = ref("");

let loadSequence = 0;

watch(
  () => props.venueId,
  loadSuppliers,
  { immediate: true }
);

onBeforeUnmount(() => {
  loadSequence++;
});

async function loadSuppliers() {
  const sequence = ++loadSequence;

  loading.value = true;
  error.value = "";

  emit("update:modelValue", "");

  const venue = String(props.venueId || "").trim();

  if (!venue) {
    suppliers.value = [];
    loading.value = false;
    return;
  }

  try {
    const [active, access] = await Promise.all([
      fetchActiveSuppliers(),
      getVenueSupplierAccess(venue),
    ]);

    if (sequence !== loadSequence) {
      return;
    }

    const activeNames =
      normaliseSupplierNames(active);

    if (access.configured) {
      const activeMap = new Map(
        activeNames.map((name) => [
          supplierKey(name),
          name,
        ])
      );

      suppliers.value =
        normaliseSupplierNames(access.suppliers)
          .map((name) =>
            activeMap.get(supplierKey(name))
          )
          .filter(Boolean);
    } else {
      suppliers.value = activeNames;
    }

    const selected = suppliers.value.find(
      (name) =>
        supplierKey(name) ===
        supplierKey(props.modelValue)
    );

    emit(
      "update:modelValue",
      selected || suppliers.value[0] || ""
    );
  } catch (cause) {
    if (sequence !== loadSequence) {
      return;
    }

    console.error(
      "[LiquorOrderingNavBar] Supplier load failed:",
      cause
    );

    suppliers.value = [];
    emit("update:modelValue", "");

    error.value =
      cause?.message ||
      "Unable to load this venue's suppliers.";
  } finally {
    if (sequence === loadSequence) {
      loading.value = false;
    }
  }
}

function selectSupplier(name) {
  if (
    suppliers.value.includes(name) &&
    name !== props.modelValue
  ) {
    emit("update:modelValue", name);
  }
}

defineExpose({
  refresh: loadSuppliers,
});
</script>

<style scoped>
.supplier-tabs-wrap {
  margin-top: 24px;
}

@media (max-width: 700px) {
  .supplier-tabs {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .supplier-tab {
    min-width: 150px;
    flex: 1 0 150px;
    width: auto;
  }
}
</style>
